import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, ShieldAlert } from 'lucide-react';
import { SortableHeader } from '../ui/SortableHeader';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { formatDate } from '../../utils/format';
import type { PairRow, PairSortField } from '../../types/customerDuplicates';
import {
    REASON_LABELS, STATUS_LABELS, MARKER_LABELS, ALL_MARKERS,
    parseSecondaryReasons, ruleLabels,
} from '../../types/customerDuplicates';
import { PairDetailPanel } from './PairDetailPanel';

interface Props {
    rows: PairRow[];
    total: number;
    loading: boolean;
    activeFilterCount: number;
    sortBy: PairSortField;
    sortOrder: 'asc' | 'desc';
    onSort: (field: PairSortField) => void;
    canEdit: boolean;
    onDecided: () => void;
}

interface Col {
    id: string;
    label: string;
    sort?: PairSortField;
    align?: 'right';
    width: number;
}

const WIDTH_KEY = 'customer-duplicates-col-widths';

const COLS: Col[] = [
    { id: 'expand', label: '', width: 28 },
    { id: 'reason', label: 'Reason', sort: 'primaryReason', width: 190 },
    { id: 'level', label: 'Level / scope', sort: 'level', width: 110 },
    { id: 'companyA', label: 'Co. A', width: 60 },
    { id: 'customerA', label: 'Customer A', width: 220 },
    { id: 'companyB', label: 'Co. B', width: 60 },
    { id: 'customerB', label: 'Customer B', width: 220 },
    { id: 'markers', label: 'Markers', width: 160 },
    { id: 'lastMovementA', label: 'Last mvmt A', width: 100 },
    { id: 'lastMovementB', label: 'Last mvmt B', width: 100 },
    { id: 'openItems', label: 'Open items', width: 90 },
    { id: 'status', label: 'Status', sort: 'status', width: 110 },
    { id: 'score', label: 'Score', sort: 'score', align: 'right', width: 70 },
];

function CustomerCell({ code, name, isBlocked }: { code: string | null; name: string | null; isBlocked: boolean }) {
    return (
        <div className="truncate" title={name ?? undefined}>
            <span className="font-mono text-xs text-gray-500">{code}</span>{' '}
            <span className="text-gray-800">{name ?? '—'}</span>
            {Boolean(isBlocked) && (
                <span title="Blocked in ERP" className="ml-1 inline-flex align-middle text-red-500">
                    <ShieldAlert className="h-3 w-3" />
                </span>
            )}
        </div>
    );
}

export function PairsTable({
    rows, total, loading, activeFilterCount, sortBy, sortOrder, onSort, canEdit, onDecided,
}: Props) {
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const sortState = { key: sortBy, direction: sortOrder };

    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            {loading && rows.length === 0 ? (
                <div className="flex h-48 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-48 items-center justify-center px-6 text-center">
                    <p className="text-sm text-gray-500">
                        {activeFilterCount > 0 ? 'No pairs match these filters.' : 'No open duplicate pairs.'}
                    </p>
                </div>
            ) : (
                <div className="max-h-[70vh] overflow-auto">
                    <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                        <colgroup>
                            {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                        </colgroup>
                        <thead>
                            <tr>
                                {COLS.map(c => c.sort ? (
                                    <SortableHeader
                                        key={c.id}
                                        label={c.label}
                                        sortKey={c.sort}
                                        sort={sortState}
                                        onSort={() => { if (!shouldSuppressClick()) onSort(c.sort!); }}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.id, x)}
                                        onResetWidth={() => resetWidth(c.id)}
                                        isResizing={resizingKey === c.id}
                                    />
                                ) : (
                                    <th key={c.id}
                                        className={`sticky top-0 z-10 relative bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                                        {c.label}
                                        {c.id !== 'expand' && (
                                            <span className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                                                onMouseDown={e => { e.preventDefault(); startResize(c.id, e.clientX); }}
                                                onDoubleClick={() => resetWidth(c.id)} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const open = expandedKey === r.pairKey;
                                const markers = parseSecondaryReasons(r.secondaryReasons)
                                    .filter(m => (ALL_MARKERS as readonly string[]).includes(m));
                                const rules = ruleLabels(r.matchedRules);
                                return (
                                    <Fragment key={r.pairKey}>
                                        <tr
                                            onClick={() => setExpandedKey(open ? null : r.pairKey)}
                                            className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                                        >
                                            <td className="px-2 py-2 text-gray-400">
                                                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="text-gray-800">{REASON_LABELS[r.primaryReason]}</div>
                                                {rules.length > 0 && (
                                                    <div className="truncate text-[10px] text-gray-400" title={rules.join(', ')}>
                                                        {rules.join(', ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                                L{r.matchLevel} · {r.scope === 'INTRA' ? 'within' : 'across'}
                                            </td>
                                            <td className="px-3 py-2 font-medium text-gray-700">{r.companyCodeA.toUpperCase()}</td>
                                            <td className="px-3 py-2">
                                                <CustomerCell code={r.customerCodeA} name={r.customerNameA} isBlocked={r.isBlockedA} />
                                            </td>
                                            <td className="px-3 py-2 font-medium text-gray-700">{r.companyCodeB.toUpperCase()}</td>
                                            <td className="px-3 py-2">
                                                <CustomerCell code={r.customerCodeB} name={r.customerNameB} isBlocked={r.isBlockedB} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {markers.slice(0, 2).map(m => (
                                                        <span key={m} title={MARKER_LABELS[m as keyof typeof MARKER_LABELS]}
                                                            className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                                                            {MARKER_LABELS[m as keyof typeof MARKER_LABELS] ?? m}
                                                        </span>
                                                    ))}
                                                    {markers.length > 2 && (
                                                        <span className="text-[10px] text-gray-400">+{markers.length - 2}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-500">
                                                {r.lastMovementA ? formatDate(r.lastMovementA) : <span className="text-gray-300">never</span>}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-500">
                                                {r.lastMovementB ? formatDate(r.lastMovementB) : <span className="text-gray-300">never</span>}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {r.openItemsA + r.openItemsB > 0 ? `${r.openItemsA} / ${r.openItemsB}` : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`text-xs ${r.status === 'OPEN' ? 'text-gray-400' : r.status === 'NOT_DUPLICATE' ? 'text-amber-700' : 'text-green-700'}`}>
                                                    {STATUS_LABELS[r.status]}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-600">{r.score}</td>
                                        </tr>
                                        {open && (
                                            <tr>
                                                <td colSpan={COLS.length} className="p-0">
                                                    <PairDetailPanel
                                                        pairKey={r.pairKey}
                                                        canEdit={canEdit}
                                                        onDecided={onDecided}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                {total.toLocaleString('de-CH')} pair{total === 1 ? '' : 's'}
            </div>
        </div>
    );
}
